<template>
    <b-overlay
        :show="isBusy"
        rounded
        opacity="0.5"
        spinner-small
        spinner-variant="primary"
        class="d-inline-block"
    >
        <span>
            <b-row>
                <b-col class="me-2">
                    Departments, Classes, Categories
                    <template v-if="treeSelection.length > 0">
                        ({{ treeSelection.length }})
                    </template>
                    <!-- <template v-if="treeSelection.length > 0">
                        <span class="float-end">
                            <b-button v-if="clearButton"
                                variant="light"
                                size="sm"
                                class="py-0"
                                @click="clearSelections"
                                >Clear
                                <b-icon-x ></b-icon-x
                            ></b-button>
                        </span>
                    </template> -->
                </b-col>
            </b-row>

            <p-tree
                :data="filteredData"
                :key="filteredData.length"
                expandable
                :include-search-filter="includeSearchFilter"
                :filter-place-holder="filterPlaceHolder"
                @selection-change="onTreeSelection"
            >
            </p-tree> </span
    ></b-overlay>
</template>

<script>
import axios from 'axios';

export default {
    name: 'DCCsSelector',
    props: {
        includeSearchFilter: {
            type: Boolean,
            default: true
        },
        clearButton: {
            type: Boolean,
            default: true
        },
    },

    data() {
        return {
            treeData: [],
            dccs: [],
            treeSelection: [],
            isBusy: false,
            filterPlaceHolder: "Search by DCC Department",
        };
    },

    computed: {
        filteredData() {
            return this.treeData;
        }
    },

    methods: {
        setTreeData() {
            //level 1 Department
            //level 2 Classes
            //level 3 Categories
            this.treeData = this.dccs.map(d => ({
                ...d,
                icon: 'fa fa-calendar',
                level: 'department',
                text: d.number + ' - ' + d.name,
                value: d.dccDepartmentId,
                children: d.dccClasses
                    .filter(c => d.dccDepartmentId === c.dccDepartmentId)
                    .map(c => ({
                        ...c,
                        icon: 'fa fa-layer-group',
                        level: 'class',
                        text: c.number + ' - ' + c.name,
                        value: c.dccClassId,
                        children: c.dccCategories
                            .filter(ca => c.dccClassId === ca.dccClassId)
                            .map(ca => ({
                                value: ca.dccId,
                                text: ca.number + ' - ' + ca.name,
                                dccClassId: c.dccClassId,
                                dccClassName: c.name,
                                dccDepartmentName: d.name,
                                dccDepartmentId: d.dccDepartmentId,
                                icon: 'fa fa-thumbtack',
                                level: 'category'
                            }))
                    }))
            }));

            if (
                this.dccs != null &&
                (this.treeData != null || this.treeData != [])
            ) {
                let selectedDepartment = this.treeData.find(
                    x => x.value == this.dccs.dccDepartmentId
                );
                if (selectedDepartment != null) {
                    selectedDepartment.selected = true;

                    let selectedClass = selectedDepartment.children.find(
                        y => y.value == this.dccs.dccClassId
                    );
                    if (selectedClass != null) {
                        selectedClass.selected = true;

                        let selectedCategory = selectedClass.dccCategories.find(
                            z => z.value == this.dccCategories.dccCategoryId
                        );
                        if (selectedCategory != null)
                            selectedCategory.selected = true;
                    }
                }

                this.onTreeSelection(this.filteredData);
            }
        },

        onTreeSelection(data) {
            this.treeSelection = data
                .flatMap(x => x.children)
                .flatMap(x => x.children)
                .filter(x => x.selected)
                .map(x => ({
                    dccId: x.value
                }));

            this.$emit('selection-change', this.treeSelection);
        },
        clearSelections(){
            this.treeSelection = [];
            this.setTreeData();
            this.$emit('selection-change', this.treeSelection);
        },

        // Restores a selection previously emitted by selection-change. p-tree keeps the selection
        // as `selected` flags on the nodes themselves, so the emitted list has to be pushed back
        // onto them. Non-matching nodes are cleared, so restoring replaces the current selection
        // rather than adding to it. Waits for the tree to load first, or there is nothing to
        // restore onto and the late load would then emit an empty selection over the caller's.
        async restoreSelections(dccIds) {
            await this.dccsLoaded;

            const restoredDccIds = new Set((dccIds ?? []).map(dcc => dcc.dccId));

            this.treeData.forEach(department => {
                department.children.forEach(dccClass => {
                    dccClass.children.forEach(category => {
                        category.selected = restoredDccIds.has(category.value);
                    });

                    // A parent is only checked when all of its children are, which is what p-tree
                    // does on a click. Without rolling this up the parent renders the
                    // indeterminate dash instead of a check. Length-guarded because a class with
                    // no categories must not count as "all selected".
                    dccClass.selected = dccClass.children.length > 0
                        && dccClass.children.every(category => category.selected);
                    dccClass.opened = dccClass.children.some(category => category.selected);
                });

                department.selected = department.children.length > 0
                    && department.children.every(dccClass => dccClass.selected);
                department.opened = department.children.some(dccClass => dccClass.selected || dccClass.opened);
            });

            // Re-emit so the caller's copy matches the tree.
            this.onTreeSelection(this.treeData);
        },

        // True when every saved dccId is still a category in the loaded tree, so a stale template
        // can be rejected rather than silently restoring a partial selection. Waits for the tree to
        // load first; checked against an empty tree, every saved dccId would look removed.
        async hasAllDccIds(dccIds) {
            await this.dccsLoaded;

            const availableDccIds = new Set(this.treeData
                .flatMap(department => department.children)
                .flatMap(dccClass => dccClass.children)
                .map(category => category.value));

            return (dccIds ?? []).every(dcc => availableDccIds.has(dcc.dccId));
        }
    },

    created: function() {
        this.dccs = [];
        this.isBusy = true;
        // Kept so restoreSelections and hasAllDccIds can wait on the load. A saved report template
        // can be applied before it finishes (My Reports launches one right after its modal opens).
        this.dccsLoaded = axios
            .get('dccs')
            .then(resp => {
                this.dccs = resp.data;
                this.setTreeData();
            })
            .finally(() => {
                this.isBusy = false;
            });
    }
};
</script>

<style scoped></style>
